import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Search, BookOpen, Calendar, ArrowRight, Sparkles, Zap, TrendingUp, Users, FileText } from 'lucide-react';
import ParticleField from './effects/ParticleField';
import TypeWriter from './effects/TypeWriter';
import AnimatedCounter from './effects/AnimatedCounter';
import { useStats } from '../hooks/useApi';
import { estimatePublicStat } from '../utils/publicStats';

const Hero: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  const apiStats = useStats();

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleSearch = () => {
    const q = searchQuery.trim();
    if (q) navigate(`/notlar?q=${encodeURIComponent(q)}`);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const notesEst = estimatePublicStat(apiStats.loaded ? apiStats.totalNotes : null, 'notes');
  const downloadsEst = estimatePublicStat(apiStats.loaded ? apiStats.totalDownloads : null, 'downloads');
  const usersEst = estimatePublicStat(apiStats.loaded ? apiStats.totalUsers : null, 'users');

  const stats = [
    { ...notesEst, label: 'Yayında not', icon: FileText, color: 'var(--accent-primary)' },
    { ...downloadsEst, label: 'İndirme', icon: TrendingUp, color: 'var(--accent-secondary)' },
    { ...usersEst, label: 'Kayıtlı üye', icon: Users, color: 'var(--accent-purple)' },
    { value: 100, prefix: '', suffix: '%', label: 'Ücretsiz erişim', icon: Zap, color: 'var(--accent-warning)' },
  ];

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1, y: 0,
      transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] },
    },
  };

  return (
    <section style={{
      minHeight: '100vh', position: 'relative', overflow: 'hidden',
      display: 'flex', alignItems: 'center',
      paddingTop: isMobile ? '80px' : '100px',
      paddingBottom: isMobile ? '40px' : 0,
    }}>
      {!isMobile && <ParticleField />}

      {/* Background glows */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
        <div style={{
          position: 'absolute', top: '10%', left: '10%', width: isMobile ? '250px' : '500px', height: isMobile ? '250px' : '500px',
          background: 'radial-gradient(circle, var(--accent-primary-soft) 0%, transparent 70%)',
          borderRadius: '50%', filter: 'blur(60px)',
        }} />
        <div style={{
          position: 'absolute', bottom: '10%', right: '10%', width: isMobile ? '200px' : '400px', height: isMobile ? '200px' : '400px',
          background: 'radial-gradient(circle, rgba(6, 182, 212, 0.06) 0%, transparent 70%)',
          borderRadius: '50%', filter: 'blur(60px)',
        }} />
      </div>

      {/* Floating shapes - desktop only */}
      {!isMobile && (
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 1 }}>
          <motion.div
            animate={{ y: [0, -20, 0], rotate: [0, 180, 360] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            style={{ position: 'absolute', top: '15%', left: '8%', width: '60px', height: '60px', border: '1px solid var(--accent-primary-soft)', borderRadius: '12px' }}
          />
          <motion.div
            animate={{ y: [0, 30, 0], rotate: [0, -90, 0] }}
            transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
            style={{ position: 'absolute', top: '25%', right: '12%', width: '40px', height: '40px', border: '1px solid rgba(6, 182, 212, 0.1)', borderRadius: '50%' }}
          />
        </div>
      )}

      <motion.div
        style={{
          maxWidth: '1200px', margin: '0 auto',
          padding: isMobile ? '0 16px' : '0 24px',
          position: 'relative', zIndex: 3, textAlign: 'center' as const, width: '100%',
          opacity,
        }}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Badge */}
        <motion.div
          variants={itemVariants}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: isMobile ? '6px' : '10px',
            padding: isMobile ? '8px 14px' : '10px 22px', borderRadius: '50px',
            fontSize: isMobile ? '12px' : '14px', fontWeight: '600', color: 'var(--accent-primary)',
            marginBottom: isMobile ? '24px' : '36px',
            background: 'var(--accent-primary-soft)', border: '1px solid var(--accent-primary-border)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <motion.span animate={{ rotate: [0, 15, -15, 0] }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}>
            <Sparkles size={isMobile ? 14 : 16} />
          </motion.span>
          <span>Türkiye'nin En Büyük Not Paylaşım Platformu</span>
          <motion.span animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1.5, repeat: Infinity }} style={{ display: 'flex' }}>
            <TrendingUp size={isMobile ? 12 : 14} />
          </motion.span>
        </motion.div>

        {/* Main Title */}
        <motion.h1
          variants={itemVariants}
          style={{
            fontSize: isMobile ? 'clamp(2.2rem, 10vw, 3rem)' : 'clamp(3rem, 7vw, 5.5rem)',
            fontWeight: '900', lineHeight: '1.05', letterSpacing: '-0.03em',
            marginBottom: '8px',
            fontFamily: "'Space Grotesk', 'Inter', sans-serif",
          }}
        >
          <span className="gradient-text-white">Paylaş, Öğren,</span>
          <br />
          <span className="gradient-text-animated" style={{ display: 'inline-block' }}>
            Birlikte Büyü
          </span>
        </motion.h1>

        {/* Typewriter subtitle */}
        <motion.div variants={itemVariants} style={{ marginBottom: isMobile ? '32px' : '48px', minHeight: isMobile ? '50px' : '60px' }}>
          <p style={{
            fontSize: isMobile ? '0.9rem' : 'clamp(1rem, 2vw, 1.25rem)',
            color: '#94a3b8', maxWidth: '700px', margin: '0 auto', lineHeight: '1.7', fontWeight: '400',
            padding: isMobile ? '0 8px' : 0,
          }}>
            Üniversite not kütüphanesi ve topluluk forumu.{' '}
            <TypeWriter
              words={['Ders konusuna göre filtrele.', 'Anında indir, başarıya ulaş.', 'Sınıf seviyesine göre ara.', 'Toplulukla birlikte öğren.']}
              style={{ color: 'var(--accent-primary)', fontWeight: '500' }}
            />
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div variants={itemVariants} style={{ maxWidth: '640px', margin: '0 auto 36px auto', padding: isMobile ? '0 4px' : 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', borderRadius: isMobile ? '14px' : '18px',
            padding: '4px', background: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(20px)',
            border: '1px solid rgba(51, 65, 85, 0.3)',
            boxShadow: '0 8px 40px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.03)',
          }}>
            <Search size={isMobile ? 18 : 20} color="#64748b" style={{ marginLeft: isMobile ? '14px' : '20px', flexShrink: 0 }} />
            <input
              type="text"
              placeholder={isMobile ? 'Ders veya konu ara...' : 'Ders, konu veya sınıf ara...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
              style={{
                flex: 1, background: 'transparent', color: 'white',
                padding: isMobile ? '14px 10px' : '18px 16px',
                border: 'none', outline: 'none', fontSize: isMobile ? '14px' : '16px', fontWeight: '400',
                minWidth: 0,
              }}
            />
            <motion.button
              type="button"
              onClick={handleSearch}
              className="btn-shimmer"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              style={{
                background: 'var(--gradient-primary)',
                color: 'white', padding: isMobile ? '12px 18px' : '16px 28px',
                borderRadius: isMobile ? '11px' : '14px', border: 'none', fontWeight: '600',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: isMobile ? '14px' : '16px',
                boxShadow: '0 4px 15px var(--accent-primary-border)', flexShrink: 0,
              }}
            >
              <span>Ara</span>
              <ArrowRight size={isMobile ? 14 : 16} />
            </motion.button>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          variants={itemVariants}
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' as const : 'column' as const,
            gap: isMobile ? '10px' : '14px',
            alignItems: 'center',
            padding: isMobile ? '0 16px' : 0,
          }}
        >
          <motion.button
            type="button"
            className="btn-shimmer pulse-glow"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollToSection('notes')}
            style={{
              background: 'var(--gradient-primary)',
              color: 'white',
              padding: isMobile ? '14px 28px' : '18px 40px',
              borderRadius: isMobile ? '14px' : '16px',
              border: 'none', fontWeight: '600', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '10px',
              fontSize: isMobile ? '15px' : '17px',
              boxShadow: '0 8px 32px var(--accent-primary-border)',
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center',
            }}
          >
            <BookOpen size={isMobile ? 18 : 20} />
            <span>Notları Keşfet</span>
            <ArrowRight size={16} />
          </motion.button>

          <motion.button
            type="button"
            whileHover={{ scale: 1.05, borderColor: 'var(--accent-primary-glow)' }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollToSection('daily-questions')}
            style={{
              background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(20px)',
              color: '#e2e8f0',
              padding: isMobile ? '14px 28px' : '18px 40px',
              borderRadius: isMobile ? '14px' : '16px',
              border: '1px solid rgba(51, 65, 85, 0.3)',
              fontWeight: '600', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '10px',
              fontSize: isMobile ? '15px' : '17px',
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center',
            }}
          >
            <Calendar size={isMobile ? 18 : 20} />
            <span>Günün Sorusu</span>
          </motion.button>
        </motion.div>

        {/* Stats */}
        <motion.div
          variants={itemVariants}
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: isMobile ? '10px' : '20px',
            marginTop: isMobile ? '48px' : '80px',
            maxWidth: '800px',
            margin: `${isMobile ? '48px' : '80px'} auto 0 auto`,
            padding: isMobile ? '0 8px' : 0,
          }}
        >
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                whileHover={isMobile ? {} : { scale: 1.08, y: -4 }}
                whileTap={isMobile ? { scale: 0.95 } : {}}
                style={{
                  textAlign: 'center' as const,
                  padding: isMobile ? '16px 10px' : '24px 16px',
                  borderRadius: isMobile ? '12px' : '16px',
                  background: 'rgba(15, 23, 42, 0.4)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(51, 65, 85, 0.15)',
                  cursor: 'default',
                }}
              >
                <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'center' }}>
                  <Icon size={isMobile ? 16 : 20} color={stat.color} style={{ opacity: 0.7 }} />
                </div>
                <AnimatedCounter
                  end={stat.value}
                  prefix={stat.prefix ?? ''}
                  suffix={stat.suffix ?? ''}
                  duration={2.5}
                  style={{
                    fontSize: isMobile ? '1.5rem' : '2rem', fontWeight: '800', color: stat.color,
                    display: 'block', fontFamily: "'Space Grotesk', sans-serif",
                  }}
                />
                <div style={{ fontSize: isMobile ? '11px' : '13px', color: '#64748b', fontWeight: '500', marginTop: '2px' }}>
                  {stat.label}
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.p
          variants={itemVariants}
          style={{
            marginTop: '12px',
            textAlign: 'center',
            fontSize: isMobile ? '11px' : '12px',
            color: 'var(--text-dim)',
          }}
        >
          * Yaklaşık topluluk değerleri
        </motion.p>

        {/* Scroll Indicator - desktop only */}
        {!isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 1 }}
            style={{ marginTop: '60px', display: 'flex', justifyContent: 'center' }}
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{
                width: '28px', height: '44px', borderRadius: '14px',
                border: '2px solid rgba(100, 116, 139, 0.3)',
                display: 'flex', justifyContent: 'center', paddingTop: '8px',
              }}
            >
              <motion.div
                animate={{ y: [0, 12, 0], opacity: [1, 0.3, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{ width: '4px', height: '8px', borderRadius: '2px', background: 'var(--accent-primary-glow)' }}
              />
            </motion.div>
          </motion.div>
        )}
      </motion.div>
    </section>
  );
};

export default Hero;
